import { dashString } from '../../utils/dashString.util';
import type { GoogleMapConfig } from '../../domain/hexa/ports/spi.model';
import type { ReadonlyLabelWithIconData, ReadonlyLabelWithIconMetadata } from '../readonly-label-with-icon';
import type { LogicalDeviceLocationAttributes } from './logicalDeviceLocationCard.model';

export interface GoogleMapLocationData {
  readonly googleMapConfig: GoogleMapConfig;
  readonly lat: string;
  readonly lng: string;
}

export function getPositionInfo({
  dateOfReport,
  displayEmptyAsDash,
  ipAddress,
  location,
  provider,
  accuracy,
}: {
  readonly dateOfReport?: string;
  readonly displayEmptyAsDash?: boolean;
  readonly hideIcon?: boolean;
  readonly location?: LogicalDeviceLocationAttributes;
  readonly ipAddress?: string;
  readonly provider?: 'GPS';
  readonly accuracy?: string;
}): ReadonlyLabelWithIconData[] {
  return dateOfReport || displayEmptyAsDash
    ? [
        {
          key: 'date-of-report',
          value: [
            ...getDisplayedLatLng(location, displayEmptyAsDash, provider),
            ...(getAccuracyValue(accuracy, displayEmptyAsDash) as ReadonlyLabelWithIconMetadata[]),
            ...(getIpAddressValue(ipAddress, displayEmptyAsDash) as ReadonlyLabelWithIconMetadata[]),
          ],
        } as ReadonlyLabelWithIconData,
      ]
    : [];
}

export function getDisplayedLatLng(
  location?: LogicalDeviceLocationAttributes,
  displayEmptyAsDash?: boolean,
  provider = 'GPS',
): ReadonlyLabelWithIconData['value'][] {
  return (location?.lat && location?.lng) || displayEmptyAsDash
    ? [
        {
          label: provider,
          value: location?.lat && location?.lng ? `${location.lat} ${location.lng}` : dashString(),
        },
      ]
    : [];
}

function getIpAddressValue(ipAddress?: string, displayEmptyAsDash?: boolean): ReadonlyLabelWithIconData['value'] {
  return ipAddress || displayEmptyAsDash
    ? [{ label: 'logicalDevice:deviceProperties.ipAddress', value: dashString(ipAddress) }]
    : [];
}

function getAccuracyValue(accuracy?: string, displayEmptyAsDash?: boolean): ReadonlyLabelWithIconData['value'] {
  return accuracy || displayEmptyAsDash
    ? [{ label: 'logicalDevice:deviceProperties.accuracy', value: dashString(accuracy ? `${accuracy} m` : undefined) }]
    : [];
}

export function getReadonlyLabelWithIconData({
  dateOfReport,
  location,
  ipAddress,
  displayEmptyAsDash,
  hideIcon,
  provider,
  accuracy,
}: {
  readonly dateOfReport?: string;
  readonly location?: LogicalDeviceLocationAttributes;
  readonly ipAddress?: string;
  readonly displayEmptyAsDash?: boolean;
  readonly hideIcon?: boolean;
  readonly provider?: 'GPS';
  readonly accuracy?: string;
}): ReadonlyLabelWithIconData[] {
  return [...getPositionInfo({ dateOfReport, displayEmptyAsDash, hideIcon, location, ipAddress, provider, accuracy })];
}

export function getGoogleMapLocationData(
  location?: LogicalDeviceLocationAttributes,
  googleMapConfig?: GoogleMapConfig,
): GoogleMapLocationData | undefined {
  if (
    location?.lat === undefined ||
    location.lng === undefined ||
    !googleMapConfig?.googleMapUrl ||
    !googleMapConfig.googleMapApi ||
    !googleMapConfig.googleMapKey
  ) {
    return undefined;
  }

  return {
    googleMapConfig,
    lat: `${location.lat}`,
    lng: `${location.lng}`,
  };
}
